/* global EpgEnabled, ProfileEnabled, UseContinuousAutoplay */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import classNames from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';
import {
  WATCH_RELATED_THUMBNAIL_IMAGE,
  WATCH_PLAYER_BACKGROUND_IMAGE,
  getDynamicImage,
  renderImage,
} from 'helpers/ImageHelper';
import { endcardVideoProcessor } from 'helpers/VideoProcessorHelper';
import { noop } from 'helpers/GlobalHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { v4 as uuidv4 } from 'uuid';
import { Endcard, GenericEndcard, VideoDetail } from 'components/VideoPlayerGlobal/components';
import { getEndCardDuration, getVideoDetailProps } from 'components/VideoPlayerGlobal/Helper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import {
  canDisplayUpsell,
  getAuthZEntitlementIssues,
  isClip,
  isMvpdSubscription,
  shouldBlockEndcardsForTrailer,
} from 'helpers/ContentHelper';
import { PAGE_COLLECTION } from 'helpers/PageHelper';
import { apiErrorHandler } from 'helpers/ApiHelper';
import {
  CONTINUOUS,
  PLAYLIST,
  RESTART,
  VOD,
  INTERRUPTION_METHODS,
  getPlayer,
  getActivePlayer,
  getPlayerState,
  isPreviewPass,
  playerControlsVisible,
  playerSetFullscreen,
  playerHideControls,
  playerPause,
  playerPlay,
  playerRemove,
  setStillWatchingHideTimeout,
  setStillWatchingShowTimeout,
  stillWatchingHideCb,
  stillWatchingShowCb,
  isPlayerTypeHero,
} from 'helpers/PlayerHelper';
import {
  getLocation,
  helperGoToVideoDetail,
  helperGetVideoUrl,
  helperGetHomeUrl,
  helperGetLiveUrl,
  helperGoToRelativeUrl,
  getPreviousPathname,
} from 'helpers/RouteHelper';
import { logPlayerError, logPlayerEvent } from 'helpers/LogReportHelpers';
import { getSessionId } from 'helpers/AuthHelper';
import {
  callSegmentPlayerEvent,
  checkRecommendationIdOnClick,
  Events,
  getRecommendationIdDataForItem,
  getSegmentPodId,
  getSegmentReferrer,
  getSegmentSourceName,
  getSegmentSourceType,
  getTealiumSourceCollectionGlobal,
  getTealiumSourceShow,
  handleAnalytics,
  PageNames,
  SegmentPlayerStates,
  SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
  SegmentVideoEvents,
  setSegmentContentPersonality,
  trackPageReferrerAnalytics,
  updateNielsenOTTStatus,
  getEndcardContentList,
  getEndCardPageEventProps,
  getVideoPageRecs,
} from '@foxcorp/delta-web-analytics';
import { RESTART_TYPE_HOME, RESTART_TYPE_LIVE } from 'screens/Watch/WatchHelpers';

import { pipeAll, get, omit } from 'helpers/Functional';

const { CONTINUOUS_PLAY, ENDCARD, HOME, LIVE, NEWS, PLAYER, PLAYER_DETAIL } = PageNames;

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    suppressNetworkIcon: false,
    defaultHideAltHeader: false,
    canHideControls: true,
    renderInfoInDetail: false,
    gridToggleLimit: 4,
    gridPrimaryToggleLimit: 4,
    gridMovieToggleLimit: 8,
    renderList: false,
    playerMetadataOverlay: true,
  },
  [SITE_PLATFORM_WEB]: {
    suppressNetworkIcon: false,
    defaultHideAltHeader: false,
    canHideControls: true,
    renderInfoInDetail: false,
    gridToggleLimit: 4,
    gridPrimaryToggleLimit: 4,
    gridMovieToggleLimit: 8,
    renderList: false,
    playerMetadataOverlay: true,
  },
  [SITE_PLATFORM_TABLET]: {
    suppressNetworkIcon: false,
    defaultHideAltHeader: true,
    canHideControls: false,
    renderInfoInDetail: true,
    gridToggleLimit: 3,
    gridPrimaryToggleLimit: 6,
    gridMovieToggleLimit: 3,
    renderList: true,
    playerMetadataOverlay: false,
    overrideShowNetworkBug: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    suppressNetworkIcon: false,
    defaultHideAltHeader: true,
    canHideControls: false,
    renderInfoInDetail: true,
    gridToggleLimit: 3,
    gridPrimaryToggleLimit: 6,
    gridMovieToggleLimit: 3,
    renderList: true,
    playerMetadataOverlay: false,
    overrideShowNetworkBug: true,
  },
};

class WatchContext extends Component {
  constructor(props) {
    super(props);
    const { panelVideo, preauthorizedResources, isFreemium } = props;
    const anonymousResumeProfileUpsell = false;
    const anonymousResume = !ProfileEnabled;
    const blockEndcardsForTrailer =
      !isFreemium && shouldBlockEndcardsForTrailer(panelVideo, preauthorizedResources);
    this.state = {
      anonymousResume,
      anonymousResumeProfileUpsell,
      blockEndcardsForTrailer,
      contentStarted: false,
      isEndCardClicked: false,
      restartFromBeginning: false,
      secondsViewed: 0,
      // showStillWatching is used in both stillWatchingHideCb and stillWatchingShowCb
      // eslint-disable-next-line react/no-unused-state
      showStillWatching: false,
      // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
      // eslint-disable-next-line react/no-unused-state
      trackedAnalyticsReferrer: false,
    };

    this.AD_INTERVAL_SEC = 1;
    this.eventVideoProps = null;
    this.playbackProps = null;
    this.endcardItem = null;
    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
    this.setStillWatchingHideTimeout = setStillWatchingHideTimeout.bind(this);
    this.setStillWatchingShowTimeout = setStillWatchingShowTimeout.bind(this);
    this.stillWatchingHideCb = stillWatchingHideCb.bind(this);
    this.stillWatchingHideTimeout = null;
    this.stillWatchingShowCb = stillWatchingShowCb.bind(this);
    this.triggeredPlaybackInterrupted = false;
  }

  componentDidMount() {
    this.triggerPlayerView();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      continuous,
      errors,
      isFreemium,
      panelVideo,
      playlist,
      restart,
      setPlayerState,
      vodClickedState,
      vodSectionClicked,
    } = this.props;
    const {
      errors: nextErrors,
      restartVideo: nextRestartVideo,
      panelVideo: nextPanelVideo,
      playerType,
      preauthorizedResources,
    } = nextProps;
    const hash = _.trim(_.get(nextProps, 'location.hash'), '#');
    const mode = _.get(nextProps, 'params.modeId', '');
    const newIsRestart = mode === RESTART || _.includes(hash, RESTART);
    // TODO: Cleanup - this logic is duplicated in Global Player, can we remove it there?
    const hashIncludesPlaylist = _.includes(hash, PLAYLIST);
    const newPlaylistId = hashIncludesPlaylist ? hash.replace(`${PLAYLIST}=`, '') : null;
    const newIsContinuous = _.includes(hash, CONTINUOUS) || hashIncludesPlaylist;
    const isRestart = restart !== newIsRestart;
    const isContinuous = continuous !== newIsContinuous;
    const isPlaylist = playlist !== newPlaylistId;
    if (isRestart || isContinuous || isPlaylist) {
      setPlayerState({
        restart: newIsRestart,
        continuous: newIsContinuous,
        playlist: newPlaylistId,
        showRestartOverlay: false,
        isVideoOverlayPresent: false,
      });
    }
    if (vodSectionClicked) {
      this.setState({ ...vodClickedState });
      const currentPlayerState = getPlayerState(getActivePlayer());
      if (
        !this.triggeredPlaybackInterrupted &&
        (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD')
      ) {
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackInterrupted,
          props: {
            ...this.videoProps,
            ...this.playbackProps,
            ...this.contentProps,
            method: INTERRUPTION_METHODS.BROWSER_ACTION,
            video_player_state: SegmentPlayerStates.PAUSE,
            session_id: this.sessionId ?? '',
            ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
            ...this.getAuthAnalyticsProps(),
          },
        });
        this.triggeredPlaybackInterrupted = true;
      }
    }

    if (newIsRestart) this.setState({ restartFromBeginning: true });

    if (panelVideo !== nextPanelVideo) {
      const blockEndcardsForTrailer =
        !isFreemium &&
        !isPlayerTypeHero(playerType) &&
        shouldBlockEndcardsForTrailer(nextPanelVideo, preauthorizedResources);

      setPlayerState({ showTrailerUpsell: false });

      this.setState({
        blockEndcardsForTrailer,
        restartFromBeginning: false,
      });
    }

    if ((errors !== nextErrors && hash) || (newIsRestart && !nextRestartVideo)) {
      if (hash === RESTART_TYPE_HOME) {
        helperGoToRelativeUrl(helperGetHomeUrl());
      } else if (_.includes(hash, RESTART_TYPE_LIVE)) {
        this.gotoLive();
      }
    }
  }

  shouldComponentUpdate(nextProps) {
    const newProps = this.getChangingProps(nextProps);
    const oldProps = this.getChangingProps(this.props);
    return !_.isEqual(newProps, oldProps);
  }

  componentDidUpdate(prevProps) {
    const {
      account,
      breakpoint,
      continuousPlayback,
      firstFramePlayed,
      mediaComplete,
      mediaEndingSeconds,
      nextVideos,
      playlist,
      playerType,
      provider,
      setVideoPanel,
      startedPlayback,
      updateShowcaseVideo,
    } = this.props;
    const {
      account: prevAccount,
      breakpoint: prevBreakpoint,
      provider: prevProvider,
      video: prevVideo,
    } = prevProps;
    const { blockEndcardsForTrailer } = this.state;
    const mvpdId = _.get(provider, 'adobePassId');
    const prevMvpdId = _.get(prevProvider, 'adobePassId');
    const mvpdIdChanged = mvpdId !== prevMvpdId;
    const userSignedInToMVPD = mvpdId && prevMvpdId && mvpdIdChanged && !isPreviewPass(mvpdId);
    const userSignedInToAccount = account && !prevAccount;
    const breakpointChanged = breakpoint !== prevBreakpoint;
    const userShowedActivity = breakpointChanged && firstFramePlayed;

    if (userShowedActivity) this.resetStillWatchingTimeout();
    if (userSignedInToAccount || userSignedInToMVPD) {
      this.setState({
        anonymousResumeProfileUpsell: null,
        restartFromBeginning: false,
      });
    }

    const mediaFinished = mediaComplete === true && mediaEndingSeconds === 0 && startedPlayback;
    const shouldPlayNextVideo = mediaFinished && continuousPlayback && !blockEndcardsForTrailer;
    if (shouldPlayNextVideo) {
      if (isPlayerTypeHero(playerType)) {
        const nextVideo = _.get(nextVideos, '0', null);
        setVideoPanel({ video: nextVideo, source: PAGE_COLLECTION });
        updateShowcaseVideo(nextVideo);
      } else {
        const nextVideoId = _.get(nextVideos, '0.id');
        const prevVideoId = _.get(prevVideo, 'id');
        const currentUrl = window.location.pathname;
        const nextUrl = helperGetVideoUrl(nextVideoId);
        const nextVideoAvailable = nextVideoId && nextVideoId !== prevVideoId;
        const urlWillChange = currentUrl !== nextUrl;

        if (nextVideoAvailable && urlWillChange) {
          const hash = playlist ? `#${PLAYLIST}=${playlist}` : `#${CONTINUOUS}`;
          helperGoToVideoDetail(nextVideoId, hash);
        }
      }
    }
  }

  componentWillUnmount() {
    const currentPlayerState = getPlayerState(getActivePlayer());
    if (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD') {
      callSegmentPlayerEvent({
        eventName: SegmentVideoEvents.videoPlaybackInterrupted,
        props: {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method: INTERRUPTION_METHODS.BROWSER_ACTION,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        },
      });
    }

    const { video, setPlayerState } = this.props;
    const item = Object.assign({}, video);
    item.videoFormat = 'vod';
    // TODO: have vod section click update this variable to true
    if (!this.ignoreUnmountReferrerAnalytics) {
      this.trackPageReferrerAnalytics({
        name: PLAYER,
      });
    }
    setPlayerState({ showTrailerUpsell: false });
  }

  getAuthAnalyticsProps = () => {
    const { accessToken, account, anonymousAccount } = this.props;
    const sid = getSessionId(accessToken || account?.accessToken || anonymousAccount?.accessToken);
    return { auth_token_sid: sid };
  };

  getEndcardSegmentData = ({ panelVideo = this.props.panelVideo } = {}) =>
    getVideoPageRecs({ endcardItems: [panelVideo], contentLength: 1 });

  onEndcardClick = opts => {
    const { nextVideos } = this.props;
    const { itemIndex, subtitleText, titleText, originalItem } = opts;
    let endcardContentLength = 0;
    this.endcardItem = originalItem;
    if (nextVideos.length) {
      endcardContentLength = nextVideos.length > 1 ? 2 : nextVideos.length;
    }
    handleAnalytics({
      event: Events.END_CARD_ITEM_SELECTED,
      payload: {
        page_action_type: itemIndex === 0 ? 'upNext' : 'recommended',
        page_item_title: titleText || undefined,
        page_item_subtitle: subtitleText || undefined,
        end_card_content_list: getEndcardContentList({
          endcardItems: nextVideos,
          headerRecommended: window.Language?.videoPage_headerRecommended ?? '',
          headerUpNext: window.Language?.videoPage_headerUpNext ?? '',
        }),
        end_card_content_length: endcardContentLength,
        end_card_selected_position: itemIndex === 0 ? 1 : itemIndex + 1,
        ...getEndCardPageEventProps({
          endcardItems: [originalItem],
          contentLength: 1,
        }),
      },
      tealiumPayload: {
        ...getCommonTealiumProps(),
      },
    });

    this.setState({ isEndCardClicked: true });
    const { setPlayerState } = this.props;
    setPlayerState({ clickedEndSlate: true }, () => {
      const { recommendationIdData, setRecommendationId } = this.props;
      const nextRecommendationIdData = getRecommendationIdDataForItem(originalItem);

      this.trackPageReferrerAnalytics({
        name: ENDCARD,
      });

      // Update recommendation id on click of endcard item if they are not the same
      checkRecommendationIdOnClick(
        recommendationIdData,
        nextRecommendationIdData,
        setRecommendationId,
      );
    });
  };

  getChangingProps = props => {
    const {
      account,
      adPlaying,
      anonymousAccount,
      anonymousId,
      blackoutDefinitions,
      breakpoint,
      clickedEndSlate,
      clickedThumbnail,
      completedPodId,
      containerId,
      continuous,
      continuousPlayback,
      creditsPlaying,
      errors,
      fetchedVideo,
      firePlayerPageViews,
      firstAdPlayed,
      firstFramePlayed,
      isAuthenticated,
      isContextPlayer,
      isFreemium,
      isFullscreen,
      isHomePage,
      location,
      mediaComplete,
      mediaEnding,
      mediaEndingSeconds,
      mediaStarted,
      nextVideos,
      panelVideo,
      playerDataHover,
      playerLoading,
      playerReference,
      playerType,
      playlist,
      portalRef,
      preauthorizedResources,
      prevVideo,
      provider,
      recommendationIdData,
      restart,
      restartPlayerScreenUrl,
      restartVideo,
      segmentReferrer,
      segmentSourceName,
      segmentSourceType,
      showControls,
      showEndCards,
      showRestartOverlay,
      startedPlayback,
      stillWatchingShowTimeout,
      theme,
      upNextVideo,
      video,
      vodClickedState,
      vodContentSet,
      vodSectionClicked,
    } = props;
    return {
      account,
      adPlaying,
      anonymousAccount,
      anonymousId,
      blackoutDefinitions,
      breakpoint,
      clickedEndSlate,
      clickedThumbnail,
      completedPodId,
      containerId,
      continuous,
      continuousPlayback,
      creditsPlaying,
      errors,
      fetchedVideo,
      firePlayerPageViews,
      firstAdPlayed,
      firstFramePlayed,
      isAuthenticated,
      isContextPlayer,
      isFreemium,
      isFullscreen,
      isHomePage,
      location,
      mediaComplete,
      mediaEnding,
      mediaEndingSeconds,
      mediaStarted,
      nextVideos,
      panelVideo,
      playerDataHover,
      playerLoading,
      playerReference,
      playerType,
      playlist,
      portalRef,
      preauthorizedResources,
      prevVideo,
      provider,
      recommendationIdData,
      restart,
      restartPlayerScreenUrl,
      restartVideo,
      segmentReferrer,
      segmentSourceName,
      segmentSourceType,
      showControls,
      showEndCards,
      showRestartOverlay,
      startedPlayback,
      stillWatchingShowTimeout,
      theme,
      upNextVideo,
      video,
      vodClickedState,
      vodContentSet,
      vodSectionClicked,
    };
  };

  onStillWatchingClose = () => {
    const { clearTimeout, playerReference, stillWatchingHideTimeout } = this.props;
    clearTimeout(stillWatchingHideTimeout);
    this.resetStillWatchingTimeout();
    // showStillWatching is used in both stillWatchingHideCb and stillWatchingShowCb
    // eslint-disable-next-line react/no-unused-state
    this.setState({ showStillWatching: false });
    playerPlay(playerReference);
  };

  getMediaInfo = () => {
    const { video, panelVideo } = this.props;
    return video || panelVideo;
  };

  calculateEndcardData(data) {
    const {
      mediaEnding: lclMediaEnding,
      mediaEndingSeconds: lclMediaEndingSeconds,
      creditsPlaying: lclCreditsPlaying,
      playerReference,
      setPlayerState,
      video,
    } = this.props;
    const newState = {};
    const { time } = data;
    const player = getPlayer(playerReference) || {};
    const { duration: currentDuration } = player;
    const creditCuePoint = _.get(video, 'creditCuePoint');
    const endCardDuration = getEndCardDuration(video);
    const endCardCuePoint = creditCuePoint || parseInt(currentDuration - endCardDuration, 10);

    if (!lclMediaEnding && time > endCardCuePoint) {
      newState.mediaEnding = true;
    }
    if (lclMediaEnding) {
      const mediaEndCuePoint = Math.min(
        endCardCuePoint + endCardDuration,
        endCardDuration + currentDuration,
      );
      const secondsUntilVideoEnd = parseInt(mediaEndCuePoint - time, 10);
      if (secondsUntilVideoEnd !== lclMediaEndingSeconds && secondsUntilVideoEnd >= 0) {
        newState.mediaEndingSeconds = secondsUntilVideoEnd;
        if (secondsUntilVideoEnd === 0 && mediaEndCuePoint !== currentDuration) {
          this.handleMediaComplete();
        }
      }
    }
    if (lclMediaEnding && lclMediaEndingSeconds && time < endCardCuePoint) {
      newState.mediaEnding = false;
      newState.mediaEndingSeconds = null;
    }
    const newCreditsPlaying = creditCuePoint && time >= creditCuePoint;
    if (lclCreditsPlaying !== newCreditsPlaying) {
      newState.creditsPlaying = newCreditsPlaying;
    }
    setPlayerState(newState);
  }

  getPlayerEvents = () => {
    const {
      defaultPlayerEvents,
      firstAdPlayed,
      floodlightEvent,
      playerReference,
      setPlayerState,
      setVideoStartedPlayback,
    } = this.props;

    return {
      ...defaultPlayerEvents,
      onReady: contentVideoData => {
        const { video } = this.props;
        const categoryTags = _.get(video, 'categoryTags', false);
        if (categoryTags) {
          setSegmentContentPersonality(categoryTags);
        }
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        this.triggeredPlaybackInterrupted = false;
        this.sessionId = uuidv4();
      },
      onAdPodStart: contentVideoData => {
        console.debug('!!watch - leapfrog onAdPodStart');
        this.adData = pipeAll(
          get('trackingData.ad', {}),
          omit('video_ad_is_skippable'),
        )(contentVideoData);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodStarted,
          props: data,
        });
        setPlayerState({
          adPlaying: true,
          playerLoading: false,
          mediaEnding: false,
          showTVRating: false,
        });
      },
      onAdPodComplete: contentVideoData => {
        setPlayerState({
          adPlaying: false,
          showTVRating: true,
          showControls: playerControlsVisible(playerReference),
        });
        console.debug('!!watch - leapfrog onAdPodComplete');
        this.adData = pipeAll(
          get('trackingData.ad', {}),
          omit('video_ad_is_skippable'),
        )(contentVideoData);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodCompleted,
          props: data,
        });
      },
      onAdSkip: contentVideoData => {
        console.debug('!!watch - leapfrog onAdSkip');
        const eventAdProps = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        const data = {
          content,
          ...this.videoProps,
          ...this.adData,
          ...eventAdProps,
          ...eventVideoProps,
          video_seconds_viewed:
            this.newAdTime - (this.newAdTime > this.previousAdTime ? this.previousAdTime : 0),
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdSkipped,
          props: data,
        });
      },
      onRemovePlayer: () => {
        const { isFullscreen } = this.props;
        setSegmentContentPersonality(false);
        if (isFullscreen) playerSetFullscreen(false, playerReference);
      },
      onFirstFrame: async () => {
        setVideoStartedPlayback(true);
        const {
          anonymousAccount: currentAnonymousAccount,
          account: currentAccount,
          anonymousLogin: currentAnonymousLogin,
          anonymousId: currentAnonymousId,
          firePlayerPageViews,
          clickedEndSlate,
          clickedThumbnail,
          mediaStarted,
          prevVideo,
          segmentReferrer: prevSegmentReferrer,
          stillWatchingShowTimeout,
        } = this.props;
        const loginAnonymousUser =
          !currentAccount &&
          !currentAnonymousAccount &&
          currentAnonymousId &&
          currentAnonymousLogin;

        if (loginAnonymousUser) {
          await currentAnonymousLogin({ deviceId: currentAnonymousId });
        }

        if (firePlayerPageViews) this.triggerPlayerView({ usePlayerPageName: true });

        clearTimeout(stillWatchingShowTimeout);
        const newStillWatchingShowTimeout = this.setStillWatchingShowTimeout({
          cb: this.showStillWatching,
          duration: this.getStillWatchingDuration(),
        });
        setPlayerState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
        const isAutoPlay = !!mediaStarted;

        if (isAutoPlay) {
          this.trackPageReferrerAnalytics({
            name: CONTINUOUS_PLAY,
          });
        }

        const { video: currentVideo } = this.props;
        const lastPath = getPreviousPathname();
        const path = lastPath || window.location.pathname;
        const sourceNameVideo = isAutoPlay ? prevVideo : currentVideo;
        const segmentSourceType = getSegmentSourceType({
          autoplay: isAutoPlay,
          endslate: clickedEndSlate,
          homeUrl: helperGetHomeUrl(),
          path,
          vodThumbnails: clickedThumbnail,
        });
        const segmentSourceName = getSegmentSourceName({
          data: clickedEndSlate || sourceNameVideo,
          type: segmentSourceType,
        });
        const segmentReferrer = getSegmentReferrer({
          prevSegmentReferrer,
        });
        setPlayerState({
          mediaComplete: false,
          mediaStarted: true,
          isAutoPlay,
          segmentSourceType,
          segmentSourceName,
          clickedEndSlate: false,
          clickedThumbnail: false,
          playerLoading: false,
          segmentReferrer,
          firstFramePlayed: true,
          firePlayerPageViews: true,
          isVideoOverlayPresent: false,
          showRestartOverlay: false,
        });
      },
      onChromecastStart: contentVideoData => {
        const castData = {
          displayName: _.get(contentVideoData, 'b.displayName', ''),
          sessionId: _.get(contentVideoData, 'b.sessionId', ''),
          status: _.get(contentVideoData, 'b.status', ''),
        };
        updateNielsenOTTStatus(true, castData);
        setVideoStartedPlayback(true);
        floodlightEvent({
          mediaInfo: this.getMediaInfo(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onChromecastComplete: () => {
        updateNielsenOTTStatus(false);
        floodlightEvent({
          mediaInfo: this.getMediaInfo(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onPlaybackStart: contentVideoData => {
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const modelMetaData = contentVideoData?.trackingData?.properties?.modelMetaData;
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          position: 0, // position is always 0 for playback start
          video_player_content_type: null,
          content_asset_id: _.get(contentVideoData, 'trackingData.content_asset_id'),
          content_pod_ids: _.get(contentVideoData, 'trackingData.content_pod_ids'),
          content_pod_id: null,
          rating: _.get(this.videoProps, 'video_rating'),
          session_id: this.sessionId ?? '',
          page_recs_collection_model_id: modelMetaData?.modelId ?? null,
          page_recs_collection_request_id: modelMetaData?.requestId ?? null,
          page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? null,
          page_recs_collection_source_id: modelMetaData?.sourceId ?? null,
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackStart,
          props: data,
        });
      },
      onPlayerError: err => {
        const { contentStarted } = this.state;
        const { panelVideo, setError } = this.props;
        const eventVideoProps = _.get(panelVideo, 'trackingData.video', {});
        const { message } = err || {};
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
        };
        const videoData = {
          video_id: _.get(panelVideo, 'id'),
          video_name: _.get(panelVideo, 'name'),
          ..._.pick(data, [
            'position',
            'livestream',
            'content_pod_id',
            'video_player_state',
            'video_asset_id',
            'video_fox_profile',
            'video_content_length',
            'video_content_type',
            'video_content_length_format',
            'video_station_id',
          ]),
          type: contentStarted ? 'videoStreamInterruptedError' : 'videoStartError',
        };
        const errorObject = new Error(message);
        errorObject.type = contentStarted ? 'videoStreamInterruptedError' : 'videoStartError';
        logPlayerError(errorObject, videoData);
        if (err?.error?.entitlementIssues || err?.data?.entitlementIssues) {
          const errors = getAuthZEntitlementIssues(err);
          setError(errors);
        }
        apiErrorHandler({ error: err?.error ?? err });
      },
      onMediaStart: () => {
        floodlightEvent({
          mediaInfo: this.getMediaInfo(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onMediaPlaying: data => {
        const { blockEndcardsForTrailer } = this.state;
        const shouldCalculateEndcards = !blockEndcardsForTrailer;
        if (shouldCalculateEndcards) this.calculateEndcardData(data);
        if (!firstAdPlayed) setPlayerState({ firstAdPlayed: true, showTVRating: true });
      },
      onContentStart: contentVideoData => {
        const { completedPodId, portalRef, setPortalContentFinished } = this.props;
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const modelMetaData = contentVideoData?.trackingData?.properties?.modelMetaData;
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          page_recs_collection_model_id: modelMetaData?.modelId ?? null,
          page_recs_collection_request_id: modelMetaData?.requestId ?? null,
          page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? null,
          page_recs_collection_source_id: modelMetaData?.sourceId ?? null,
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        const videoData = {
          video_id: _.get(contentVideoData, 'id'),
          video_name: _.get(contentVideoData, 'name'),
          ..._.pick(data, [
            'position',
            'livestream',
            'content_pod_id',
            'video_player_state',
            'video_asset_id',
            'video_fox_profile',
            'video_content_length',
            'video_content_type',
            'video_content_length_format',
            'video_station_id',
          ]),
        };
        this.setState({ contentStarted: true });
        if (portalRef) setPortalContentFinished(false);
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentStart,
          props: data,
        });
        logPlayerEvent('onVideoPlaybackStart', videoData);
        if (completedPodId) setPlayerState({ completedPodId: null });
        window?.fennecInstance?.deltaContentStart(contentVideoData);
      },
      onContentPlaying: contentVideoData => {
        const { secondsViewed: previouslyViewed } = this.state;
        const secondsViewed = _.get(contentVideoData, 'trackingData.contentSecondsViewed', 0);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        if (
          secondsViewed !== previouslyViewed &&
          secondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC === 0
        ) {
          const data = {
            ...this.videoProps,
            ...this.contentProps,
            ...eventVideoProps,
            video_seconds_viewed: SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
            session_id: this.sessionId ?? '',
            ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoContentPlaying,
            props: data,
          });
          window?.fennecInstance?.deltaContentPlaying(contentVideoData);
        }
        if (secondsViewed !== previouslyViewed) {
          const videoPlayhead = { [this.sessionId ?? '']: secondsViewed };
          if (window) _.set(window, '_segHBPlayheads', videoPlayhead);
        }
        this.setState({ secondsViewed });
      },
      onAdPlaying: contentVideoData => {
        console.debug('!!watch - leapfrog onAdPlaying');
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        if (this.newAdTime !== this.previousAdTime && this.newAdTime % this.AD_INTERVAL_SEC === 0) {
          const eventAdProps = _.get(contentVideoData, 'trackingData.ad', {});
          const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
          const content = {
            ..._.pick(this.contentProps, [
              'airdate',
              'asset_id',
              'full_episode',
              'program',
              'title',
            ]),
            ..._.pick(this.videoProps, [
              'load_type',
              'position',
              'total_length',
              'video_content_length',
              'video_nielsen_clientid',
              'video_nielsen_subbrand',
              'video_tms_id',
            ]),
          };
          const data = {
            content,
            ...this.videoProps,
            ...this.adData,
            ...eventAdProps,
            ...eventVideoProps,
            video_seconds_viewed:
              this.newAdTime - (this.newAdTime > this.previousAdTime ? this.previousAdTime : 0),
            session_id: this.sessionId ?? '',
            ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoAdPlaying,
            props: data,
          });
        }
        this.previousAdTime = this.newAdTime;
      },
      onAdComplete: contentVideoData => {
        console.debug('!!watch - leapfrog onAdComplete');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const props = {
          content,
          ...this.videoProps,
          ...this.adData,
          video_seconds_viewed:
            this.newAdTime === this.previousAdTime ? 0 : this.newAdTime - this.previousAdTime,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdCompleted,
          props,
        });
      },
      onAdPause: contentVideoData => {
        console.debug('!!watch - leapfrog onAdPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const adVideoProps = _.get(contentVideoData, 'trackingData.ad', {});
        const data = {
          ...this.videoProps,
          ...eventVideoProps,
          ...adVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onAdResume: contentVideoData => {
        console.debug('!!watch - leapfrog onAdResume');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.eventProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.eventProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdResumed,
          props: data,
        });
      },
      onPause: contentVideoData => {
        console.debug('!!watch - leapfrog onPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onResume: contentVideoData => {
        console.debug('!!watch - leapfrog onResume');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackResumed,
          props: data,
        });
      },
      onBufferStart: contentVideoData => {
        console.debug('!!watch - leapfrog onBufferStart');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferStarted,
          props: data,
        });
      },
      onBufferEnd: contentVideoData => {
        console.debug('!!watch - leapfrog onBufferEnd');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferComplete,
          props: data,
        });
      },
      onContentComplete: contentVideoData => {
        console.debug('!!watch - leapfrog onContentComplete');
        const totalSecondsViewed = _.get(contentVideoData, 'trackingData.contentSecondsViewed', 0);
        const secondsViewed = Math.floor(totalSecondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC);
        const completedPodId = getSegmentPodId(contentVideoData);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          video_seconds_viewed: secondsViewed,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        setPlayerState({ completedPodId });
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentComplete,
          props: data,
        });
        this.setState({ contentStarted: false });
      },
      onMediaComplete: data => {
        const { clearInterval, mediaEndingSeconds, setInterval } = this.props;
        const { blockEndcardsForTrailer } = this.state;
        if (blockEndcardsForTrailer) {
          this.handleMediaComplete();
          return;
        }
        const newState = {};
        let remainingTimeAfterMediaComplete = mediaEndingSeconds;
        const remainingTimeCountdown = setInterval(() => {
          const { isEndCardClicked } = this.state;
          if (isEndCardClicked) {
            this.setState({ isEndCardClicked: false });
            clearInterval(remainingTimeCountdown);
          }
          remainingTimeAfterMediaComplete -= 1;
          newState.mediaEndingSeconds = remainingTimeAfterMediaComplete;
          if (
            remainingTimeAfterMediaComplete <= 0 ||
            Number.isNaN(remainingTimeAfterMediaComplete)
          ) {
            clearInterval(remainingTimeCountdown);
            this.handleMediaComplete({ data });
          } else {
            setPlayerState(newState);
          }
        }, 1000);
      },
      onPlaybackComplete: contentVideoData => {
        const { portalRef, setPortalContentFinished, upNextVideo } = this.props;
        const firstUpnextVideo = upNextVideo?.[0];
        this.endcardItem = firstUpnextVideo;
        console.debug('!!watch - leapfrog onPlaybackComplete');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };

        data.video_player_content_type = null;
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackComplete,
          props: data,
        });
        setPlayerState({ completedPodId: null });
        if (portalRef) setPortalContentFinished(true);
      },
      onPlaybackInterrupted: data => {
        console.debug('!!watch - leapfrog onPlaybackInterrupted');
        const { method } = data;
        const props = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackInterrupted,
          props,
        });
      },
      onSeekStart: contentVideoData => {
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        data.position = Math.round(contentVideoData.start);
        data.seek_position = Math.round(contentVideoData.end);
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackSeekStarted,
          props: data,
        });
      },
      onSeekComplete: contentVideoData => {
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getEndcardSegmentData({ panelVideo: this.endcardItem }),
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackSeekCompleted,
          props: data,
        });
      },
      onUserInteraction: () => {
        const { clearTimeout, stillWatchingShowTimeout } = this.props;
        clearTimeout(stillWatchingShowTimeout);
        const newStillWatchingShowTimeout = this.setStillWatchingShowTimeout({
          cb: this.showStillWatching,
          duration: this.getStillWatchingDuration(),
        });
        setPlayerState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
      },
      onFullScreen: e => {
        const { fullscreen } = e;
        setPlayerState({ isFullscreen: fullscreen });
        this.videoProps.video_is_fullscreen = fullscreen;
        this.playbackProps.full_screen = fullscreen;
      },
      onChangeContinuous: e => {
        const isContinuous = _.get(e, 'isContinuous', false);
        if (!UseContinuousAutoplay) return;
        setPlayerState({ continuousPlayback: isContinuous });
      },
      onVolumeChange: ({ newVolume, oldVolume }) => {
        const { video_volume } = this.videoProps;
        if (video_volume === newVolume || oldVolume === newVolume) return null;
        this.videoProps.video_volume = newVolume;
        this.playbackProps.sound = newVolume;
        return null;
      },
      // OnPlayerTypeChange
      // This is likely needed for analytics in the future.
      // It fires for player.updatePlayerType(TYPE)
    };
  };

  getStillWatchingDuration = () => {
    const durationSeconds = _.get(window.ExternalConfig, 'stillWatching.vodLimitSeconds');
    return durationSeconds ? durationSeconds * 1000 : null;
  };

  gotoLive = () => {
    const hash = _.trim(_.get(this.props, 'location.hash'), '#');
    const callSign = _.trim(hash.replace(RESTART_TYPE_LIVE, ''), '-');
    helperGoToRelativeUrl(helperGetLiveUrl({ callSign }));
  };

  handleMediaComplete = (opts = {}) => {
    const { data } = opts;
    const { playerReference, restartVideo, setPlayerState, preauthorizedResources } = this.props;
    const { blockEndcardsForTrailer } = this.state;
    const nextProgramRights = _.get(data, 'nextProgramRights', false);
    if (restartVideo) {
      const secondsViewed = _.get(data, 'secondsViewed', 0);
      const videoDuration = _.get(restartVideo, 'durationInSeconds', 0);
      const watchDuration = videoDuration / 2;
      const videoWatched = secondsViewed > watchDuration;
      const handleEvent = !nextProgramRights || videoWatched;
      if (!handleEvent) return;
      setPlayerState({
        showRestartOverlay: true,
        isVideoOverlayPresent: true,
      });
      playerRemove(playerReference);
      return;
    }
    const shouldShowEndCards = !nextProgramRights;
    const shouldShowTrailerUpsell =
      blockEndcardsForTrailer && canDisplayUpsell(preauthorizedResources);
    setPlayerState({
      mediaComplete: true,
      mediaEnding: false,
      mediaEndingSeconds: 0,
      firstFramePlayed: false,
      showEndCards: shouldShowEndCards,
      showTrailerUpsell: shouldShowTrailerUpsell,
      creditsPlaying: false,
    });
  };

  resetResumeWithProfile = (cb = _.noop) => {
    const nextState = {
      anonymousResume: null,
      restartFromBeginning: true,
    };
    this.setState(nextState, cb);
  };

  resetStillWatchingTimeout = () => {
    const {
      breakpoint,
      clearTimeout,
      firstFramePlayed,
      setPlayerState,
      stillWatchingShowTimeout,
    } = this.props;
    clearTimeout(stillWatchingShowTimeout);
    const isWeb = breakpoint === SITE_PLATFORM_WEB;
    const isWebLarge = breakpoint === SITE_PLATFORM_WEB_LARGE;
    const isDesktop = isWeb || isWebLarge;
    if (isDesktop && firstFramePlayed) {
      const newStillWatchingShowTimeout = this.setStillWatchingShowTimeout({
        cb: this.showStillWatching,
        duration: this.getStillWatchingDuration(),
      });
      setPlayerState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
    }
  };

  resume = () => {
    this.setState({ anonymousResumeProfileUpsell: null, anonymousResume: null });
  };

  showStillWatching = () => {
    const {
      breakpoint,
      account,
      clearTimeout,
      firstFramePlayed,
      playerReference,
      setPlayerState,
      stillWatchingShowTimeout,
    } = this.props;
    const profileId = _.get(account, 'profileId');
    const isWeb = breakpoint === SITE_PLATFORM_WEB;
    const isWebLarge = breakpoint === SITE_PLATFORM_WEB_LARGE;
    const isDesktop = isWeb || isWebLarge;
    clearTimeout(stillWatchingShowTimeout);
    if (isDesktop && firstFramePlayed) {
      playerSetFullscreen(false, playerReference);
      playerPause(playerReference);
      playerHideControls(playerReference);
      this.stillWatchingShowCb({ profileId });
      const stillWatchingHideTimeout = this.setStillWatchingHideTimeout({
        cb: () => {
          playerPlay(playerReference);
          this.stillWatchingHideCb();
          window.location = helperGetHomeUrl();
        },
      });
      setPlayerState({ stillWatchingHideTimeout });
    }
  };

  triggerPlayerView = ({ usePlayerPageName } = {}) => {
    const { pageViewEvent, getUseUtagGlobal, isHomePage, playerType } = this.props;
    const previousPath = getPreviousPathname();
    const location = getLocation(window.referrer);
    const referrerPath = _.get(location, 'pathname');
    const useUtagGlobal = getUseUtagGlobal();
    const collection = { ...getTealiumSourceCollectionGlobal() };
    const isMovieSource = referrerPath.match(/^\/(movies)(\/)?(.*)$/);
    const isLiveSource = referrerPath.match(/^\/(live)(\/)?(.*)$/);
    const isWatchSource = referrerPath.match(/^\/(watch)(\/)?(.*)$/);
    const isNews = previousPath?.match(/^\/(news)(\/)?(.*)$/);
    const isShowSource = getTealiumSourceShow();
    const isVideoPage = isMovieSource || isLiveSource || isWatchSource || isShowSource;
    const isDirectSource = previousPath === '' && document.referrer === '';
    const isPlayerHero = isPlayerTypeHero(playerType);
    let pageName = _.get(window.utag_data, 'page_name');

    if (isNews) {
      pageName = NEWS;
    } else if (pageName) {
      // Use utag_data.page_name
    } else if (isPlayerHero && isHomePage) {
      pageName = HOME;
    } else if (usePlayerPageName) {
      pageName = PLAYER;
    } else if (isDirectSource || isWatchSource) {
      if (!isPlayerHero)
        handleAnalytics({
          event: Events.PLAYER_LANDING,
          payload: {
            pageName: `player`,
            pageType: `player`,
          },
        });
      pageName = PLAYER;
    } else if (isMovieSource || isShowSource) {
      pageName = PLAYER_DETAIL;
    } else if (isLiveSource) {
      pageName = LIVE;
    }

    if (isVideoPage) pageViewEvent({ collection, pageName, useUtagGlobal });
  };

  renderEndcards() {
    const {
      breakpoint,
      continuousPlayback,
      creditsPlaying,
      isAuthenticated,
      isFullscreen,
      mediaComplete,
      mediaEnding,
      mediaEndingSeconds,
      nextVideos,
      playerType,
      restartVideo,
      showEndCards,
      video,
    } = this.props;
    const { blockEndcardsForTrailer } = this.state;

    const shouldDisplayEndcard =
      !restartVideo &&
      !blockEndcardsForTrailer &&
      (!continuousPlayback || ((mediaEnding || mediaComplete || creditsPlaying) && showEndCards));
    const endCardDuration = getEndCardDuration(video);
    const isEnding =
      (mediaEndingSeconds && mediaEndingSeconds <= endCardDuration) || creditsPlaying;
    const shouldRender = isEnding && shouldDisplayEndcard && !isPlayerTypeHero(playerType);

    return shouldRender ? (
      <Endcard
        continuousPlayback={continuousPlayback}
        endcardItems={nextVideos}
        renderImage={image =>
          renderImage({
            breakpoint,
            imageType: WATCH_RELATED_THUMBNAIL_IMAGE,
            ...image,
          })
        }
        video={video}
        mediaEndingSeconds={mediaEndingSeconds}
        videoProcessor={endcardVideoProcessor}
        isAuthenticated={isAuthenticated}
        isFullscreen={isFullscreen}
        onClick={this.onEndcardClick}
      />
    ) : null;
  }

  renderRestartOverlay() {
    const { panelVideo, showRestartOverlay } = this.props;
    if (!showRestartOverlay) return null;

    const {
      playback_restartRestricted: liveHeader,
      playback_viewOthers: liveCTA,
    } = window.Language;

    const action = () => (EpgEnabled ? this.gotoLive() : helperGoToRelativeUrl(helperGetHomeUrl()));
    const fallbackBackground = _.get(panelVideo, 'images.seriesStill.raw');
    const background = _.get(panelVideo, 'images.still.raw', fallbackBackground);
    return (
      <GenericEndcard action={action} background={background} cta={liveCTA} header={liveHeader} />
    );
  }

  renderVideoDetail(opts = {}) {
    const {
      adPlaying,
      breakpoint,
      creditsPlaying,
      favoritesManager,
      isContextPlayer,
      isFullscreen,
      panelVideo,
      playerType,
      setPlayerDataHover,
      setStickyControls,
      theme,
      video,
    } = this.props;
    const { renderInfoInDetail } = BREAKPOINTS[breakpoint];
    const { showControls, hasEndcard } = opts;

    if (isPlayerTypeHero(playerType) || hasEndcard) return null;
    const classes = classNames(isFullscreen ? theme.fullscreen : false, theme.watchDetailContainer);

    return (
      <div className={classes}>
        <VideoDetail
          {...getVideoDetailProps(panelVideo)}
          adPlaying={adPlaying}
          favoritesManager={favoritesManager}
          isContextPlayer={isContextPlayer}
          renderInfo={renderInfoInDetail}
          setPlayerDataHover={setPlayerDataHover}
          setStickyControls={setStickyControls}
          showControls={showControls}
          showNetworkIcon={!creditsPlaying}
          video={video}
        />
      </div>
    );
  }

  render() {
    const {
      account,
      breakpoint,
      continuous,
      fetchedVideo,
      openSignIn,
      openSignUp,
      panelVideo,
      playerDataHover,
      playerLoading,
      playerType,
      restart,
      restartVideo,
      restartPlayerScreenUrl,
      showControls,
      renderVideoContainer,
      savePercentWatched,
      subscriptionPaused,
      vodContentSet,
      isFullscreen,
      containerId,
    } = this.props;
    const {
      anonymousResume,
      anonymousResumeProfileUpsell,
      restartFromBeginning,
      showStillWatching,
    } = this.state;
    if (!fetchedVideo) return null;

    const { canHideControls } = BREAKPOINTS[breakpoint];
    const { creditCuePoint, videoType } = fetchedVideo;
    const fallbackBackground = getDynamicImage({
      breakpoint,
      context: WATCH_PLAYER_BACKGROUND_IMAGE,
      url: _.get(fetchedVideo, 'images.still.raw'),
    });
    const seriesListImage = fetchedVideo?.images?.seriesList?.raw ?? fallbackBackground;
    const seriesStillImage = fetchedVideo?.images?.seriesStill?.raw ?? fallbackBackground;
    const background = getDynamicImage({
      breakpoint,
      context: WATCH_PLAYER_BACKGROUND_IMAGE,
      url: seriesStillImage,
    });
    const roadBlockbackground = getDynamicImage({
      breakpoint,
      context: WATCH_PLAYER_BACKGROUND_IMAGE,
      url: seriesListImage,
    });
    const videoPercentWatched = _.get(fetchedVideo, 'percentWatched', 0);
    const resetPercentWatched =
      isClip(videoType) || videoPercentWatched >= 98 || restartFromBeginning;
    const percentWatched = resetPercentWatched ? 0 : videoPercentWatched;
    const videoViewed = percentWatched > 0 && percentWatched < 100;
    const showResumeProfileUpsell = !!(
      !account &&
      videoViewed &&
      anonymousResumeProfileUpsell &&
      vodContentSet &&
      !isMvpdSubscription()
    );
    const showResumeCTA = videoViewed && anonymousResume;

    const resumeWithProfileConfig = {
      playFromBeginning: () => {
        this.resetResumeWithProfile();
      },
      play: this.resume,
      reset: this.resetResumeWithProfile,
    };
    const mediaInfo = this.getMediaInfo();
    const playerConfig = {
      ...this.getPlayerEvents(),
      continuous,
      creditCuePoint,
      mediaInfo,
      percentWatched,
      restart,
      restartPlayerScreenUrl,
    };

    const showVideoDetail =
      panelVideo &&
      ((!canHideControls && !isFullscreen) ||
        playerLoading ||
        showControls ||
        (playerDataHover && !isFullscreen));

    const contextProps = {
      roadBlockbackground,
      background,
      continuous,
      creditCuePoint,
      mediaInfo,
      mode: restartVideo ? RESTART : VOD,
      onStillWatchingClose: this.onStillWatchingClose,
      openSignIn,
      openSignUp,
      percentWatched,
      playerConfig,
      playerLoadingImage: background,
      requestSignIn: showResumeProfileUpsell,
      restart,
      resumeOnCancel: this.resume,
      resumeWithProfileConfig,
      savePercentWatched,
      showControls: showVideoDetail,
      showResumeCTA,
      showStillWatching,
      subscriptionPaused,
      type: playerType,
      isFullscreen,
      containerId,
    };

    const endCards = this.renderEndcards();
    const restartOverlay = this.renderRestartOverlay();
    const videoDetail = this.renderVideoDetail({
      showControls: showVideoDetail,
      hasEndcard: !!endCards,
    });
    return (
      <>
        {renderVideoContainer({ props: contextProps, endCards, videoDetail })}
        {restartOverlay}
      </>
    );
  }
}

WatchContext.defaultProps = {
  accessToken: null,
  account: null,
  adPlaying: null,
  anonymousAccount: {},
  anonymousId: null,
  anonymousLogin: noop,
  clearTimeout: noop,
  clickedEndSlate: null,
  clickedThumbnail: null,
  completedPodId: null,
  containerId: null,
  continuous: null,
  continuousPlayback: null,
  creditsPlaying: null,
  errors: null,
  favoritesManager: null,
  fetchedVideo: null,
  firePlayerPageViews: null,
  firstAdPlayed: null,
  firstFramePlayed: null,
  isAuthenticated: null,
  isContextPlayer: false,
  isFullscreen: null,
  isHomePage: false,
  location: null,
  mediaComplete: null,
  mediaEnding: null,
  mediaEndingSeconds: null,
  mediaStarted: null,
  panelVideo: null,
  playerLoading: null,
  playlist: null,
  portalRef: null,
  preauthorizedResources: null,
  prevVideo: {},
  provider: null,
  recommendationIdData: null,
  restart: null,
  restartPlayerScreenUrl: null,
  restartVideo: null,
  segmentReferrer: null,
  segmentSourceName: null,
  segmentSourceType: null,
  setRecommendationId: noop,
  setVideoPanel: noop,
  showEndCards: null,
  showRestartOverlay: null,
  startedPlayback: null,
  stillWatchingHideTimeout: noop,
  stillWatchingShowTimeout: null,
  upNextVideo: null,
  video: null,
  vodClickedState: {},
  vodSectionClicked: false,
};

WatchContext.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  adPlaying: PropTypes.bool,
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func,
  breakpoint: PropTypes.string.isRequired,
  clearInterval: PropTypes.func.isRequired,
  clearTimeout: PropTypes.func,
  clickedEndSlate: PropTypes.bool,
  clickedThumbnail: PropTypes.bool,
  completedPodId: PropTypes.string,
  containerId: PropTypes.string,
  continuous: PropTypes.bool,
  continuousPlayback: PropTypes.bool,
  creditsPlaying: PropTypes.bool,
  defaultPlayerEvents: PropTypes.shape({}).isRequired,
  errors: PropTypes.number,
  favoritesManager: PropTypes.shape({}),
  fetchedVideo: PropTypes.shape({}),
  firePlayerPageViews: PropTypes.bool,
  firstAdPlayed: PropTypes.bool,
  firstFramePlayed: PropTypes.bool,
  floodlightEvent: PropTypes.func.isRequired,
  getUseUtagGlobal: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isContextPlayer: PropTypes.bool,
  isFreemium: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  isHomePage: PropTypes.bool,
  location: PropTypes.shape({}),
  mediaComplete: PropTypes.bool,
  mediaEnding: PropTypes.bool,
  mediaEndingSeconds: PropTypes.number,
  mediaStarted: PropTypes.bool,
  nextVideos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  openSignIn: PropTypes.func.isRequired,
  openSignUp: PropTypes.func.isRequired,
  pageViewEvent: PropTypes.func.isRequired,
  panelVideo: PropTypes.shape({}),
  playerDataHover: PropTypes.bool.isRequired,
  playerLoading: PropTypes.bool,
  playerReference: PropTypes.string,
  playerType: PropTypes.string.isRequired,
  playlist: PropTypes.bool,
  portalRef: PropTypes.string,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  prevVideo: PropTypes.shape({}),
  provider: PropTypes.shape({}),
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  renderVideoContainer: PropTypes.func.isRequired,
  restart: PropTypes.bool,
  restartPlayerScreenUrl: PropTypes.string,
  restartVideo: PropTypes.shape({}),
  savePercentWatched: PropTypes.func.isRequired,
  segmentReferrer: PropTypes.shape({}),
  segmentSourceName: PropTypes.string,
  segmentSourceType: PropTypes.string,
  setError: PropTypes.func,
  setInterval: PropTypes.func.isRequired,
  setPlayerDataHover: PropTypes.func.isRequired,
  setPlayerState: PropTypes.func.isRequired,
  setPortalContentFinished: PropTypes.func,
  setRecommendationId: PropTypes.func,
  setStickyControls: PropTypes.func.isRequired,
  setVideoPanel: PropTypes.func,
  setVideoStartedPlayback: PropTypes.func.isRequired,
  showControls: PropTypes.bool.isRequired,
  showEndCards: PropTypes.bool,
  showRestartOverlay: PropTypes.bool,
  startedPlayback: PropTypes.bool,
  stillWatchingHideTimeout: PropTypes.func,
  stillWatchingShowTimeout: PropTypes.number,
  subscriptionPaused: PropTypes.bool,
  theme: PropTypes.shape({}).isRequired,
  updateShowcaseVideo: PropTypes.func,
  upNextVideo: PropTypes.arrayOf(PropTypes.shape({})),
  video: PropTypes.shape({}),
  vodClickedState: PropTypes.shape({}),
  vodContentSet: PropTypes.bool.isRequired,
  vodSectionClicked: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    upNextVideo: state.globalVideo.nextVideos,
    vodClickedState: state.globalVideo.vodClickedState,
    vodContentSet: state.globalVideo.vodContentSet,
    vodSectionClicked: state.globalVideo.vodSectionClicked,
  };
}

const TimeoutWatchContext = ReactTimeout(connect(mapStateToProps, {})(WatchContext));

export default TimeoutWatchContext;
